<script lang="ts">
	import { createEventDispatcher, getContext } from "svelte";
	import { locateByValue } from "@xbs/lib-dom";
	import { elementsIds } from "@xbs/lib-kanban";
	import type {
		ICard,
		ICardShape,
		TEventDispatcher,
		TMenuItemsFn,
	} from "@xbs/lib-kanban";

	import Icon from "../lib/Icon.svelte";
	import Progress from "../lib/Progress.svelte";
	import Menu from "./../lib/Menu.svelte";

	import Footer from "./cardFields/Footer.svelte";
	import Header from "./cardFields/Header.svelte";

	export let cardFields: ICard;
	export let cardShape: ICardShape;

	const _ = getContext<any>("wx-i18n").getGroup("kanban");

	const dispatch: TEventDispatcher = createEventDispatcher();

	$: menuOpen = false;

	function handleMenuClick() {
		menuOpen = !menuOpen;

		if (menuOpen) {
			dispatch("action", {
				action: "open-card-menu",
				data: { id: cardFields.id },
			});
		}
	}
	function handleMenuAction(e: any) {
		const action = e.detail.id;
		const menuItem = e.detail.item;

		if (action === "delete-card") {
			dispatch("action", {
				action: "delete-card",
				data: { id: cardFields.id },
			});
		}

		if (action === "set-edit") {
			dispatch("action", {
				action: "set-edit",
				data: { cardId: cardFields.id },
			});
		}

		if (action === "duplicate-card") {
			dispatch("action", {
				action: "duplicate-card",
				data: {
					id: cardFields.id,
					card: {
						label: `${_("Duplicate of")} ${cardFields.label}`,
					},
				},
			});
		}

		if (menuItem.onClick) {
			menuItem.onClick({
				id: action,
				item: menuItem,
				card: cardFields,
			});
		}
		menuOpen = false;
	}

	$: coverAttachment = cardFields?.attached?.find(obj => obj.isCover);
	$: coverURL = coverAttachment
		? coverAttachment.coverURL || coverAttachment.url
		: null;

	let menuEl: HTMLElement;
	let kanbanContentEl: HTMLElement;

	$: kanbanContentEl =
		menuEl && locateByValue(menuEl, elementsIds.content, "data-kanban-id");

	function getMenuCoords() {
		const menuRect = menuEl.getBoundingClientRect();
		const kanbanContentRect = kanbanContentEl.getBoundingClientRect();

		return {
			x: menuRect.x + kanbanContentEl.scrollLeft,
			y:
				menuRect.y -
				kanbanContentRect.y +
				menuRect.height +
				kanbanContentEl.scrollTop,
		};
	}

	const getMenuItems = (card: ICard, cardShape: ICardShape) => {
		const menuItems = (
			cardShape.menu.items as TMenuItemsFn<{ card: ICard }>
		)({
			card,
		});

		if (!menuItems || !menuItems.length) {
			return null;
		}

		return menuItems.map(item => {
			return {
				...item,
				label: _(item.label),
			};
		});
	};

	$: menuItems = getMenuItems(cardFields, cardShape);
	$: isMenuVisible = cardShape.menu.show && !!menuItems;

</script>

{#if cardShape.color?.show && cardFields.color}
	<div class="wx-color wx-rounded" style="background:{cardFields.color}" />
{/if}
{#if cardShape?.cover?.show && coverURL}
	<div
		class="wx-field wx-image"
		class:wx-rounded={!(cardShape.color?.show && cardFields.color)}>
		<img src={coverURL} alt="" />
	</div>
{/if}
<div class="wx-content">
	<Header {cardFields} {cardShape} />
	<div class="wx-body">
		<div class="wx-field wx-label">
			{#if cardShape?.label?.show && cardFields.label}
				<span> {cardFields.label} </span>
			{/if}
			{#if isMenuVisible}
				<div
					class="wx-menu"
					bind:this={menuEl}
					data-ignore-selection="true">
					<Icon name="wxi-dots-v" click={handleMenuClick} />
					{#if menuOpen}
						<Menu
							coords={getMenuCoords()}
							data={menuItems}
							on:click={handleMenuAction}
							container={kanbanContentEl}
							cancel={() => {
								menuOpen = null;
							}} />
					{/if}
				</div>
			{/if}
		</div>
		{#if cardShape?.description?.show && cardFields.description}
			<div class="wx-field wx-description">{cardFields.description}</div>
		{/if}

		{#if cardShape?.progress?.show && cardFields.progress}
			<div class="wx-field">
				<Progress
					min={cardShape.progress?.config?.min || 0}
					max={cardShape.progress?.config?.max || 100}
					value={cardFields.progress} />
			</div>
		{/if}
	</div>

	<Footer {cardFields} {cardShape} />
</div>

<style>
	.wx-content {
		padding: 20px;

		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		justify-content: center;
		position: relative;
	}

	.wx-color {
		height: 5px;
		min-height: 5px;
		width: 100%;
	}
	.wx-rounded {
		border-radius: 6px 6px 0 0;
	}
	.wx-field {
		margin-bottom: var(--wx-kanban-card-field-padding);
	}

	.wx-body .wx-field:last-child {
		margin-bottom: 0;
	}
	.wx-label {
		font-weight: 500;
		font-size: var(--wx-font-size-md);
		min-height: 1em;
		max-width: 100%;
		position: relative;
		padding-right: 10px;
		word-break: break-word;
	}
	.wx-description {
		font-size: var(--wx-font-size);
		/* [todo] uses non standart properties, try to implement another way */
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	.wx-image {
		overflow: hidden;
		width: 100%;
		height: 150px;
		margin-bottom: 0;
	}
	.wx-image img {
		width: 100%;
		height: 100%;
	}
	.wx-menu {
		position: absolute;
		top: 0;
		right: -5px;
	}

</style>
